import React, { useState } from 'react';
import { Alert, Box, Button, Collapse, Typography } from '@mui/material';
import { InfoOutlined as InfoIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { CalibrationCheck } from '../../../services/pours';

interface CalibrationNoticeProps {
  /** The pour against its calibration (ASTM C1074 9.2). */
  check: CalibrationCheck | null | undefined;
  /** The pour computes maturity (pourComputesMaturity): only those pours
   *  get the notice, as on desktop. */
  computesMaturity: boolean;
  /** The Overview tab: the only one that shows 'unknown'. */
  overview: boolean;
}

/**
 * The phone dashboard's ASTM C1074 9.2 notice, under the header:
 *  - mismatch: an error, always expanded, on every tab (strength is not
 *    shown for the pour);
 *  - differs: a 44px warning row with Details, on every tab;
 *  - unknown: a 44px info row with Details, on the Overview only.
 * A match, no calibration, or a pour that computes no maturity: nothing.
 */
export const CalibrationNotice: React.FC<CalibrationNoticeProps> = ({ check, computesMaturity, overview }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!check?.text || !computesMaturity) return null;

  if (check.status === 'mismatch') {
    return (
      <Alert severity="error" data-testid="mobile-calibration-notice" sx={{ mb: 1.5 }}>
        <Typography component="div" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {t('mobile.dashboard.calibration.mismatchTitle', { defaultValue: 'Strength not shown' })}
        </Typography>
        <Typography component="div" sx={{ fontSize: '0.875rem' }}>
          {check.text}
        </Typography>
      </Alert>
    );
  }

  const differs = check.status === 'differs';
  if (!differs && !(check.status === 'unknown' && overview)) return null;
  const tone = differs
    ? { bg: '#FAEEDA', fg: '#9a6a00', Icon: WarningAmberIcon }
    : { bg: '#E6F1FB', fg: '#185FA5', Icon: InfoIcon };
  const title = differs
    ? t('mobile.dashboard.calibration.differsTitle', { defaultValue: 'Calibration inputs differ (ASTM C1074 9.2)' })
    : t('mobile.dashboard.calibration.unknownTitle', { defaultValue: 'Calibration basis not recorded' });

  return (
    <Box
      data-testid="mobile-calibration-notice"
      data-status={check.status}
      sx={{ mb: 1.5, borderRadius: 2, bgcolor: tone.bg, color: tone.fg, minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 44, pl: 1.5, pr: 0.5 }}>
        <tone.Icon aria-hidden sx={{ fontSize: 20, flexShrink: 0 }} />
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: 600 }}>{title}</Typography>
        <Button
          color="inherit"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          sx={{ minHeight: 44, minWidth: 44, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
        >
          {open
            ? t('mobile.dashboard.calibration.hide', { defaultValue: 'Hide' })
            : t('mobile.dashboard.calibration.details', { defaultValue: 'Details' })}
        </Button>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Typography sx={{ px: 1.5, pb: 1.5, fontSize: '0.875rem', color: 'text.primary' }}>{check.text}</Typography>
      </Collapse>
    </Box>
  );
};

export default CalibrationNotice;
